import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCirclesCandidates,
  fetchCandidateDetails,
  fetchCompanyInfo,
  fetchCandidateActivities,
  fetchCandidateNotes,
  addCandidateNotes,
  removeCandidateTags,
  addCandidateTags,
  updateCandidateNotes,
} from './actions';
import { PAGINATION } from '@/constants/pagination';

const initialState = {
  isCandidatesLoading: true,
  circleCandidates: [],
  circleCandidatesPagination: {},

  isCandidateDetailsLoading: false,
  candidateDetails: {},

  isCompanyInfoLoading: true,
  companyInfo: null,

  isActivitiesLoading: true,
  activities: [],

  isNotesLoading: true,
  notes: [],

  isNewNoteSaving: false,

  page: {
    number: 0,
    offset: 0,
    limit: PAGINATION.CONTACTS,
  },

  filters: {
    sync: false,
    changed: false,
    location: [],
    jobTitle: [],
    tags: [],
    skills: [],
    user: [],
    pastCompanySelected: false,
  },

  sort: {
    element: 'name',
    order: 'asc',
  },
  ignoreId: 0,
};

export const { actions, reducer } = createSlice({
  name: 'circlesCandidates',
  initialState,

  reducers: {
    applyFilter: (state, action) => {
      const { type, selected = [] } = action.payload;
      state.page.number = 0;
      state.page.offset = 0;
      state.page.limit = PAGINATION.CONTACTS;
      state.filters.changed = true;
      state.filters[type] = selected;
    },

    clearFilter: (state, action) => {
      const { type, isMulti = false } = action.payload;
      state.filters.changed = false;
      state.filters[type] = isMulti ? [] : null;
    },

    applyPage: (state) => {
      state.page.number = state.page.number + 1;
      state.page.offset = state.page.number * state.page.limit;
    },

    applySort: (state, action) => {
      const { element = 'name', order = 'asc' } = action.payload;
      state.page = { ...initialState.page };
      state.sort.element = element;
      state.sort.order = order;
    },

    applySecondDegreeConnection: (state, action) => {
      const { ignoreId } = action.payload;
      state.ignoreId = ignoreId;
      state.page = { ...initialState.page };
      state.circleCandidatesPagination = {};
    },

    resetPage: (state) => {
      state.circleCandidatesPagination = {};
      state.page.number = 0;
      state.page.offset = 0;
      state.page.limit = PAGINATION.CONTACTS;
      state.filters.sync = false;
    },

    persistCirclesFilter: (state, { payload }) => {
      const {
        location,
        jobTitle,
        tags,
        skills,
        user,
        circleChanged,
        currUser,
        pastCompany,
        companyId,
      } = payload;
      state.page.number = 0;
      state.page.offset = 0;
      state.page.limit = PAGINATION.CONTACTS;
      state.circleCandidatesPagination = {};
      state.filters.location = location;
      state.filters.jobTitle = jobTitle;
      state.filters.tags = tags;
      state.filters.skills = skills;
      state.filters.user = circleChanged ? user : [currUser];
      if (
        pastCompany?.length &&
        pastCompany?.find((comp) => comp?.id === Number(companyId))
      ) {
        state.filters.pastCompanySelected = true;
      } else {
        state.filters.pastCompanySelected = false;
      }
      state.filters.sync = true;
      state.filters.changed = circleChanged ? true : false;
    },
    clearAllFilter: (state) => {
      const isFilterSynced = state.filters.sync;
      const user = state.filters.user;
      state.filters = {
        ...initialState.filters,
        sync: isFilterSynced,
        user,
        changed: false,
      };
      state.page = initialState.page;
      state.ignoreId = initialState?.ignoreId;
    },

    resetCachedCandidates: (state, { payload: { candidateId, contact } }) => {
      if (candidateId) {
        const currContact = state?.circleCandidates;
        const { first_name, last_name } = contact;
        const updatedContacts = currContact.map((con) => {
          if (con.id === candidateId) {
            return { ...con, first_name, last_name };
          }
          return con;
        });
        state.circleCandidates = updatedContacts;
      }
      state.candidateDetails = {};
    },
  },
  extraReducers: {
    [fetchCirclesCandidates.pending]: (state) => {
      state.isCandidatesLoading = true;
    },
    [fetchCirclesCandidates.fulfilled]: (state, { payload }) => {
      state.isCandidatesLoading = false;
      state.circleCandidatesPagination = payload?.data?.pagination;
      if (state.page.number === 0) {
        state.circleCandidates = payload?.data?.contact || [];
        return;
      }
      if (payload?.data?.contact?.length) {
        state.circleCandidates = [
          ...state.circleCandidates,
          ...payload?.data?.contact,
        ];
      }
    },
    [fetchCandidateDetails.pending]: (state) => {
      state.isCandidateDetailsLoading = true;
    },
    [fetchCandidateDetails.fulfilled]: (state, { payload }) => {
      const { contact } = payload?.data;
      state.candidateDetails[contact.id] = contact;
      state.isCandidateDetailsLoading = false;
    },

    [fetchCompanyInfo.pending]: (state) => {
      state.isCompanyInfoLoading = true;
    },
    [fetchCompanyInfo.fulfilled]: (state, { payload }) => {
      state.isCompanyInfoLoading = false;
      state.companyInfo = payload?.data?.company;
    },

    [fetchCandidateActivities.pending]: (state) => {
      state.isActivitiesLoading = true;
    },

    [fetchCandidateActivities.fulfilled]: (state, { payload }) => {
      state.isActivitiesLoading = false;
      state.activities = payload?.data;
    },

    [fetchCandidateNotes.pending]: (state) => {
      state.isNotesLoading = true;
    },

    [fetchCandidateNotes.fulfilled]: (state, { payload }) => {
      state.isNotesLoading = false;
      state.notes = payload?.data.note;
    },

    [addCandidateNotes.pending]: (state) => {
      state.isNewNoteSaving = true;
    },

    [addCandidateNotes.fulfilled]: (state, { payload }) => {
      state.isNewNoteSaving = false;
      state.notes = [payload?.data.note, ...state.notes];
    },

    [addCandidateTags.fulfilled]: (state, { payload }) => {
      const { contactId, data: tags } = payload;
      const candidateDetails = state.candidateDetails;
      const currCandidate = { ...candidateDetails[contactId], ...tags };
      state.candidateDetails[contactId] = currCandidate;
    },

    [removeCandidateTags.fulfilled]: (state, { payload }) => {
      const { contactId, data: tags } = payload;
      const candidateDetails = state.candidateDetails;
      const currCandidate = { ...candidateDetails[contactId], ...tags };
      state.candidateDetails[contactId] = currCandidate;
    },

    [updateCandidateNotes.fulfilled]: (state, { payload, meta }) => {
      const {
        arg: { noteId },
      } = meta;
      const updatedNote = payload?.data?.note;
      const currNotes = state.notes;
      const updatedNotes = currNotes?.map((note) => {
        if (note.id === noteId) {
          return updatedNote;
        }
        return note;
      });
      state.notes = updatedNotes;
    },
  },
});

export const {
  applyPage,
  resetPage,
  persistCirclesFilter,
  clearAllFilter,
  applyFilter,
  applySort,
  clearFilter,
  resetCachedCandidates,
  applySecondDegreeConnection,
} = actions;
export default reducer;
